import { ONBOARDING_ACADEMY_VIDEOS } from '../generated/onboarding-academy-videos.generated'
import {
  GRAFTCODE_ONBOARDING_GOAL,
  GRAFTCODE_ONBOARDING_SCENARIO,
  type GraftcodeOnboardingScenarioId,
} from '../graftcode-onboarding.const'
import type { GraftcodeOnboardingDashboardVideo } from '../graftcode-onboarding-videos.const'
import { buildOnboardingYoutubeEmbedSrc } from '../graftcode-onboarding-videos.const'
import {
  type PersonalizeOnboardingCoursesInput,
  scoreScenarioPathFromAnchor,
} from '../mappers/getPersonalizedOnboardingCourses'

const SCORE_STACK_MATCH = 1000
const SCORE_COMPLETED_SCENARIO = -1000

const ONBOARDING_TOPIC_ID_TO_SCENARIO: Record<string, GraftcodeOnboardingScenarioId> = {
  'connect-frontend-to-backend': GRAFTCODE_ONBOARDING_SCENARIO.frontend,
  'expose-backend': GRAFTCODE_ONBOARDING_SCENARIO.backend,
  'connect-microservices': GRAFTCODE_ONBOARDING_SCENARIO.microservices,
  'use-modules-from-any-technology': GRAFTCODE_ONBOARDING_SCENARIO.modules,
  'expose-mcp': GRAFTCODE_ONBOARDING_SCENARIO.mcp,
  'switch-between-monolith-and-microservices': GRAFTCODE_ONBOARDING_SCENARIO.monolith,
}

function resolveAnchorScenarioId(input: PersonalizeOnboardingCoursesInput): GraftcodeOnboardingScenarioId | null {
  return input.primaryScenarioId ?? input.completedScenarioIds[input.completedScenarioIds.length - 1] ?? null
}

export function scoreOnboardingDashboardVideo(
  topicId: string,
  stackId: string,
  input: PersonalizeOnboardingCoursesInput
): number {
  const scenarioId = ONBOARDING_TOPIC_ID_TO_SCENARIO[topicId]
  if (!scenarioId) {
    return SCORE_COMPLETED_SCENARIO
  }

  const completed = new Set(input.completedScenarioIds)
  if (completed.has(scenarioId)) {
    return SCORE_COMPLETED_SCENARIO
  }

  const anchor = resolveAnchorScenarioId(input)

  let score = 0
  if (input.lastCompletedStackId && stackId === input.lastCompletedStackId) {
    score += SCORE_STACK_MATCH
  }
  score += scoreScenarioPathFromAnchor(scenarioId, anchor)

  if (input.goalId === GRAFTCODE_ONBOARDING_GOAL.ai) {
    if (scenarioId === GRAFTCODE_ONBOARDING_SCENARIO.mcp) {
      score += 6
    }
    if (scenarioId === GRAFTCODE_ONBOARDING_SCENARIO.backend) {
      score += 3
    }
  }

  if (input.goalId === GRAFTCODE_ONBOARDING_GOAL.integrations) {
    if (scenarioId === GRAFTCODE_ONBOARDING_SCENARIO.frontend) {
      score += 5
    }
    if (scenarioId === GRAFTCODE_ONBOARDING_SCENARIO.microservices) {
      score += 4
    }
  }

  return score
}

export function getPersonalizedOnboardingDashboardVideos(
  input: PersonalizeOnboardingCoursesInput,
  limit = 4
): GraftcodeOnboardingDashboardVideo[] {
  const pool =
    input.lastCompletedStackId != null
      ? ONBOARDING_ACADEMY_VIDEOS.filter((video) => video.stackId === input.lastCompletedStackId)
      : [...ONBOARDING_ACADEMY_VIDEOS]

  const candidates = pool.length > 0 ? pool : [...ONBOARDING_ACADEMY_VIDEOS]

  const scored = candidates
    .map((video, index) => ({
      video,
      index,
      score: scoreOnboardingDashboardVideo(video.topicId, video.stackId, input),
    }))
    .filter((item) => item.score > SCORE_COMPLETED_SCENARIO)
    .sort((left, right) => {
      if (right.score !== left.score) {
        return right.score - left.score
      }
      return left.index - right.index
    })

  return scored.slice(0, limit).map(({ video }) => ({
    id: video.id,
    title: video.title,
    thumbSrc: video.thumbSrc,
    embedSrc: buildOnboardingYoutubeEmbedSrc(`https://www.youtube-nocookie.com/embed/${video.youtubeVideoId}`),
  }))
}
